import styles from './RichText.module.scss';
import cx from 'classnames';
import { Caption } from 'ui/components/1-atoms/Caption';
import { Link } from 'ui/components/1-atoms/Action/Link';
import { Picture } from 'ui/components/1-atoms/Media/Picture';
import ReactHtmlParser, { convertNodeToElement } from 'react-html-parser';
import { Tag } from 'ui/components/2-molecules/Tag';
import { SignImageUrl } from 'helpers/getImageUrl';

export interface RichTextProps {
	content: string;
	className?: string;
	onScroll?: (e: React.UIEvent<HTMLDivElement>) => void;
	forwardedRef?: React.RefObject<HTMLDivElement>;
}

export const RichText: React.FC<RichTextProps> = ({ className, content, onScroll, forwardedRef }) => {
	const transform = (node, index) => {
		const isComponentTag = node.attribs?.['data-component-tag'] === 'true';

		if (node.type === 'tag' && node.name === 'iframe' && node.attribs) {
			// Umbraco RTE embeds (YouTube/Vimeo/etc.) still emit the legacy `allowtransparency`
			// attribute, which React 18 no longer recognises and warns about. Strip it (plus the
			// vendor-prefixed fullscreen variants) and let the default converter render the iframe.
			delete node.attribs.allowtransparency;
			delete node.attribs.webkitallowfullscreen;
			delete node.attribs.mozallowfullscreen;
			return undefined;
		}

		if (node.type === 'tag' && node.name === 'a') {
			const children = node.children?.map((child, childIndex) =>
				convertNodeToElement(child, childIndex, transform),
			);

			return isComponentTag ? (
				<Tag key={index} link={{ url: node.attribs.href, ...node.attribs }}>
					{children}
				</Tag>
			) : (
				<Link key={index} url={node.attribs.href} {...node.attribs}>
					{children}
				</Link>
			);
		}

		if (node.type === 'tag' && node.name === 'img' && node.attribs.src) {
			// Mst.dk is just a dummy domain, as we only want relative URL's
			// const src = new URL(node.attribs.src, 'http://mst.dk');

			// Remove query params provided from the CMS because the Picture component
			// will handle them instead
			// src.search = '';

			// We have changed the UrlModeSwitcher to output images with relative URLs or absolute URLs.
			// That way we dont have to make a relative url for the Picture component.

			// Sign the CMS-provided URL so ImageSharp accepts the resize params when HMAC is enabled.
			return <Picture key={index} url={SignImageUrl(node.attribs?.src)} altText={node.attribs.alt} isCover={false} useProvidedImageUrl={true} />;
		}

		if (node.type === 'tag' && node.name === 'figcaption' && node.children[0]?.data) {
			return <Caption key={index}>{node.children[0].data}</Caption>;
		}

		if (isComponentTag) {
			const componentProps = node.attribs?.['data-component-props'];
			const clipboardCopy = node.attribs?.['data-clipboard-copy'];
			const props = componentProps ? JSON.parse(componentProps) ?? {} : {};

			return (
				<Tag
					key={index}
					{...props}
					clickHandler={clipboardCopy ? () => navigator?.clipboard?.writeText(clipboardCopy) : undefined} // Note: Only works in secure contexts (https)
				>
					{node.children[0].data}
				</Tag>
			);
		}
	};

	const parsedContent = ReactHtmlParser(content, {
		transform,
	});

	return (
		<div className={cx(styles.RichText, className)} onScroll={onScroll} ref={forwardedRef}>
			{parsedContent}
		</div>
	);
};